/**
 * Auth session storage — thin wrapper over localStorage.
 * Token key is owned by the shared API client (AUTH_TOKEN_KEY, "pg_auth_token");
 * this module adds the companion user-profile key written at login.
 */
import { AUTH_TOKEN_KEY } from "../../shared/api/client";

export const AUTH_USER_KEY = "pg_auth_user";

export type AccountScope = "platform" | "company";

export interface AuthUser {
  id?: number;
  username?: string;
  email?: string;
  role?: string;
  tenant_id?: number | null;
  tenant_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  /** Explicit API field: platform (PayGO) vs company (tenant staff). */
  account_scope?: AccountScope;
  account_scope_label?: string;
  is_platform_user?: boolean;
  is_company_user?: boolean;
  /** Effective permission strings from JWT / /auth/me (e.g. "payment.process"). */
  permissions?: string[];
}

export function getToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function getUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

export function setSession(token: string, user?: AuthUser): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
  if (user) localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
}

export function clearSession(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
}

/** Human label for the "Signed in as …" indicator. */
export function userDisplayName(user: AuthUser | null): string {
  if (!user) return "";
  const full = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  return full || user.username || "";
}

/** Pretty-print the role enum (e.g. "commercial_officer" → "Commercial Officer"). */
export function userDisplayRole(user: AuthUser | null): string {
  if (!user?.role) return "";
  return user.role
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Platform (PayGO) user vs company (tenant) user.
 * Prefer explicit account_scope from API; fall back to role === system_admin.
 */
export function accountScope(user: AuthUser | null): AccountScope | null {
  if (!user) return null;
  if (user.account_scope === "platform" || user.account_scope === "company") {
    return user.account_scope;
  }
  if (user.is_platform_user === true) return "platform";
  if (user.is_company_user === true) return "company";
  return user.role === "system_admin" ? "platform" : "company";
}

/** PayGO platform operator (cross-tenant). Company staff always have a tenant_id. */
export function isPlatformAdmin(user: AuthUser | null): boolean {
  return accountScope(user) === "platform";
}

/**
 * Check if the user holds a specific permission string.
 * Falls back gracefully when permissions are not yet loaded.
 */
export function hasPermission(user: AuthUser | null | undefined, permission: string): boolean {
  if (!user?.permissions) return false;
  return user.permissions.includes(permission);
}

/**
 * Short scope line for chrome:
 * - Platform: "Platform user · PayGO (all companies)"
 * - Company: "Company user · GreenLeaf Energy" (or Company #id)
 */
export function userScopeLabel(user: AuthUser | null): string {
  if (!user) return "";
  if (user.account_scope_label?.trim()) return user.account_scope_label.trim();
  if (isPlatformAdmin(user)) return "Platform operator · PayGO";
  const name = user.tenant_name?.trim();
  if (name) return `Company user · ${name}`;
  if (user.tenant_id != null) return `Company user · #${user.tenant_id}`;
  return "Company user";
}
